import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & FieldProps>(
  ({ label, hint, error, leftIcon, className, ...rest }, ref) => (
    <label className="block">
      {label && <span className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 dark:text-gray-300">{label}</span>}
      <div className="relative">
        {leftIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{leftIcon}</span>
        )}
        <input
          ref={ref}
          {...rest}
          className={cn(
            'w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100 dark:placeholder:text-gray-500',
            leftIcon && 'pl-9',
            error && 'border-red-300 focus:ring-red-500',
            className,
          )}
        />
      </div>
      {hint && !error && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </label>
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & FieldProps>(
  ({ label, hint, error, className, ...rest }, ref) => (
    <label className="block">
      {label && <span className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 dark:text-gray-300">{label}</span>}
      <textarea
        ref={ref}
        {...rest}
        className={cn(
          'w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px] dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100 dark:placeholder:text-gray-500',
          error && 'border-red-300 focus:ring-red-500',
          className,
        )}
      />
      {hint && !error && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </label>
  ),
);
Textarea.displayName = 'Textarea';

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & FieldProps>(
  ({ label, hint, error, className, children, ...rest }, ref) => (
    <label className="block">
      {label && <span className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 dark:text-gray-300">{label}</span>}
      <select
        ref={ref}
        {...rest}
        className={cn(
          'w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100',
          error && 'border-red-300 focus:ring-red-500',
          className,
        )}
      >
        {children}
      </select>
      {hint && !error && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </label>
  ),
);
Select.displayName = 'Select';
